import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine, Area, AreaChart, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";
import {
  BarChart3, TrendingUp, TrendingDown, Award, Flame, Zap, Layers, CalendarDays,
  Brain, Rocket, FileText, Users2,
} from "lucide-react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast as _toast } from "sonner";
import { useAccounts } from "@/context/AccountContext";
import BacktestWizard from "@/components/BacktestWizard";
import WeeklyDigestModal from "@/components/WeeklyDigestModal";

const DIMENSIONS = [
  { value: "pattern", label: "Pattern" },
  { value: "session", label: "Session" },
  { value: "timeframe", label: "Timeframe" },
  { value: "symbol", label: "Symbol" },
  { value: "dow", label: "Day of Week" },
  { value: "hour", label: "Hour (UTC)" },
];

const KPI = ({ label, value, sub, icon: Icon, tone = "info", testId }) => {
  const c =
    tone === "buy" ? "#10b981" :
    tone === "sell" ? "#ef4444" :
    tone === "warn" ? "#f59e0b" :
    "#3b82f6";
  return (
    <div className="vx-card p-4" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="vx-label">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: c }} strokeWidth={1.75} />}
      </div>
      <div className="mt-2 font-mono text-2xl font-bold tracking-tight" style={{ color: c }}>
        {value}
      </div>
      {sub && <div className="mt-1 text-[10px] text-[#6b7280] font-mono">{sub}</div>}
    </div>
  );
};

const money = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  const s = n >= 0 ? "+" : "";
  return `${s}$${n.toFixed(2)}`;
};

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded border border-[#1f2937] bg-[#0a0e1a] px-3 py-2 text-xs font-mono">
      <div className="text-[#9ca3af]">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2" style={{ color: p.color }}>
          {p.name}: <span className="font-bold">{typeof p.value === "number" ? p.value.toFixed(2) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [breakdownDim, setBreakdownDim] = useState("pattern");
  const [breakdown, setBreakdown] = useState([]);
  const [equity, setEquity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [learning, setLearning] = useState(null);
  const [learningRows, setLearningRows] = useState([]);
  const [showBacktest, setShowBacktest] = useState(false);
  const [showDigest, setShowDigest] = useState(false);
  const [pool, setPool] = useState(null);
  const { activeAccountId, activeAccount, refresh: refreshAccounts } = useAccounts();

  const loadPool = async () => {
    try {
      const r = await api.get("/learning/pool");
      setPool(r.data);
    } catch { /* silent */ }
  };

  const togglePoolAccount = async (accountId, on) => {
    const current = new Set(pool?.pooled_account_ids || []);
    if (on) current.add(accountId); else current.delete(accountId);
    const next = Array.from(current);
    try {
      await api.put("/learning/pool", { pooled_account_ids: next });
      setPool((p) => ({ ...p, pooled_account_ids: next }));
      _toast.success(on ? "Added to pool — future signals will share learned edge weights" : "Removed from pool");
    } catch {
      _toast.error("Failed to update learning pool");
    }
  };

  const loadLearning = async () => {
    try {
      const [s, rows] = await Promise.all([
        api.get("/analytics/learning"),
        api.get("/analytics/learning/rows"),
      ]);
      setLearning(s.data);
      setLearningRows(rows.data?.rows || []);
    } catch { /* silent */ }
  };

  const toggleLearning = async (on) => {
    if (!activeAccountId) return;
    try {
      await api.put(`/accounts/${activeAccountId}/learning`, { use_learned_weights: on });
      _toast.success(on ? "Learning ON — new signals will use tuned weights" : "Learning OFF");
      await refreshAccounts();
      await loadLearning();
    } catch (e) {
      _toast.error("Failed to toggle learning");
    }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const [s, e] = await Promise.all([
          api.get(`/analytics/summary?days=${days}`),
          api.get(`/journal?days=${days}&pnl=all`),
        ]);
        if (!alive) return;
        setSummary(s.data);

        // Build equity curve from closed journal entries (chronological)
        const entries = (e.data.entries || [])
          .filter((x) => x.outcome === "win" || x.outcome === "loss")
          .sort((a, b) => new Date(a.closed_at) - new Date(b.closed_at));
        let running = 0;
        const curve = entries.map((t) => {
          running += Number(t.pnl || 0);
          return {
            t: new Date(t.closed_at).toLocaleDateString([], { month: "short", day: "2-digit" }),
            equity: Number(running.toFixed(2)),
            pnl: Number(t.pnl || 0),
            symbol: t.symbol,
          };
        });
        setEquity(curve);
      } catch (err) {
        toast.error("Failed to load analytics");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [days]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/analytics/breakdown?dimension=${breakdownDim}&days=${days}`);
        if (alive) setBreakdown(r.data.items || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [breakdownDim, days]);

  useEffect(() => { loadLearning(); }, [activeAccountId]);
  useEffect(() => { loadPool(); }, []);

  const kpiTone = useMemo(() => {
    if (!summary) return {};
    return {
      pnl: summary.total_pnl >= 0 ? "buy" : "sell",
      expect: summary.expectancy_per_trade >= 0 ? "buy" : "sell",
    };
  }, [summary]);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="vx-label">Performance</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">
            Analytics
          </h1>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl">
            All metrics computed from closed journal entries. Cross-cut win rates and P&L by pattern,
            session, timeframe, or time of day to spot your edge.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDigest(true)}
            data-testid="open-digest-btn"
            className="h-9 px-3 rounded border border-[#a78bfa]/40 hover:bg-[#a78bfa]/10 text-xs font-mono text-[#a78bfa] flex items-center gap-1.5"
          >
            <FileText className="w-3.5 h-3.5" /> Weekly Digest
          </button>
          <button
            onClick={() => setShowBacktest(true)}
            data-testid="open-backtest-btn"
            className="h-9 px-3 rounded bg-[#3b82f6] hover:bg-[#2563eb] text-xs font-mono text-white flex items-center gap-1.5"
          >
            <Rocket className="w-3.5 h-3.5" /> Run Backtest
          </button>
          <span className="vx-label">Window</span>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger
              className="w-[140px] h-9 bg-[#111827] border-[#1f2937] text-xs font-mono"
              data-testid="days-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last 365 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {learning && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="vx-card p-4 xl:col-span-2" data-testid="auto-learning-card">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded flex items-center justify-center bg-[#a78bfa]/15 border border-[#a78bfa]/40">
                <Brain className="w-4 h-4 text-[#a78bfa]" />
              </div>
              <div>
                <div className="vx-label">Auto-Learning</div>
                <div className="text-sm text-[#e5e7eb] mt-0.5">
                  {learning.high_confidence} <span className="text-[#6b7280]">high</span> · {learning.medium_confidence} <span className="text-[#6b7280]">medium</span> · {learning.low_confidence} <span className="text-[#6b7280]">low-confidence</span> profiles across {learning.profiles_count} pattern/session/timeframe combos.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-[#9ca3af]">Apply to new signals</span>
              <Switch
                checked={!!learning.use_learned_weights}
                onCheckedChange={toggleLearning}
                data-testid="learning-toggle"
              />
            </div>
          </div>

          {(learning.strongest || learning.weakest) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {learning.strongest && (
                <div className="p-3 rounded border border-[#10b981]/40 bg-[#10b981]/10 text-xs font-mono">
                  <div className="text-[10px] uppercase tracking-widest text-[#10b981] font-bold">Strongest edge</div>
                  <div className="mt-1 text-[#e5e7eb] font-bold text-sm">
                    {learning.strongest.pattern_type} · {learning.strongest.session} · {learning.strongest.timeframe}
                  </div>
                  <div className="text-[#9ca3af] text-[11px] mt-0.5">
                    {learning.strongest.trades_count} trades · win {(learning.strongest.win_rate * 100).toFixed(1)}% · weight {learning.strongest.learned_weight}×
                  </div>
                </div>
              )}
              {learning.weakest && (
                <div className="p-3 rounded border border-[#ef4444]/40 bg-[#ef4444]/10 text-xs font-mono">
                  <div className="text-[10px] uppercase tracking-widest text-[#ef4444] font-bold">Weakest zone</div>
                  <div className="mt-1 text-[#e5e7eb] font-bold text-sm">
                    {learning.weakest.pattern_type} · {learning.weakest.session} · {learning.weakest.timeframe}
                  </div>
                  <div className="text-[#9ca3af] text-[11px] mt-0.5">
                    {learning.weakest.trades_count} trades · win {(learning.weakest.win_rate * 100).toFixed(1)}% · weight {learning.weakest.learned_weight}×
                  </div>
                </div>
              )}
            </div>
          )}

          {learningRows.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-[10px] uppercase tracking-widest text-[#6b7280] hover:text-[#9ca3af]">Show all {learningRows.length} rows</summary>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-xs font-mono" data-testid="learning-rows-table">
                  <thead className="text-left text-[#6b7280]">
                    <tr><th className="py-1">Pattern</th><th className="py-1">Session</th><th className="py-1">TF</th><th className="py-1 text-right">Trades</th><th className="py-1 text-right">Win%</th><th className="py-1 text-right">Weight</th><th className="py-1 text-right">Confidence</th></tr>
                  </thead>
                  <tbody>
                    {learningRows.map((r) => (
                      <tr key={r.id} className="border-t border-[#1f2937]">
                        <td className="py-1 text-[#e5e7eb]">{r.pattern_type}</td>
                        <td className="py-1 text-[#9ca3af]">{r.session}</td>
                        <td className="py-1 text-[#9ca3af]">{r.timeframe}</td>
                        <td className="py-1 text-right">{r.trades_count}</td>
                        <td className="py-1 text-right" style={{ color: r.win_rate >= 0.5 ? "#10b981" : "#f59e0b" }}>{(r.win_rate * 100).toFixed(1)}%</td>
                        <td className="py-1 text-right font-bold" style={{ color: r.learned_weight >= 1 ? "#10b981" : "#ef4444" }}>{r.learned_weight}×</td>
                        <td className="py-1 text-right">
                          <span className="text-[9px] uppercase tracking-widest" style={{ color: r.sample_size_confidence === "high" ? "#10b981" : r.sample_size_confidence === "medium" ? "#f59e0b" : "#6b7280" }}>
                            {r.sample_size_confidence}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>

        {/* Phase 6 — Learning Pool card */}
        <div className="vx-card p-4" data-testid="learning-pool-card">
          <div className="flex items-start gap-2 mb-3">
            <div className="w-8 h-8 rounded flex items-center justify-center bg-[#f59e0b]/15 border border-[#f59e0b]/40">
              <Users2 className="w-4 h-4 text-[#f59e0b]" />
            </div>
            <div>
              <div className="vx-label">Learning Pool</div>
              <div className="text-xs text-[#9ca3af] mt-0.5 leading-relaxed">
                Pool accounts running similar strategies to share pattern insights. When ≥ 2 accounts are pooled, learned weights use the summed sample size — a much stronger signal than any single account.
              </div>
            </div>
          </div>
          {!pool ? (
            <div className="text-xs font-mono text-[#6b7280]">Loading pool…</div>
          ) : (
            <div className="space-y-1">
              {(pool.accounts || []).map((a) => {
                const inPool = (pool.pooled_account_ids || []).includes(a.id);
                return (
                  <label
                    key={a.id}
                    className="flex items-center justify-between py-2 px-2 rounded hover:bg-[#1f2937]/40 cursor-pointer"
                    data-testid={`pool-row-${a.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm truncate">{a.account_name}</div>
                      <div className="text-[10px] font-mono uppercase text-[#6b7280] tracking-widest">{a.broker_id}</div>
                    </div>
                    <Switch
                      checked={inPool}
                      onCheckedChange={(v) => togglePoolAccount(a.id, v)}
                      data-testid={`pool-toggle-${a.id}`}
                    />
                  </label>
                );
              })}
              <div className="pt-2 border-t border-[#1f2937] text-[10px] font-mono text-[#6b7280]">
                {(pool.pooled_account_ids || []).length >= 2
                  ? `✓ Pool active with ${pool.pooled_account_ids.length} accounts`
                  : "Add at least 2 accounts to activate pooled learning"}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {loading || !summary ? (
        <div className="text-[#9ca3af] font-mono text-sm">Loading analytics…</div>
      ) : summary.total_trades === 0 ? (
        <div className="vx-card p-10 text-center">
          <BarChart3 className="w-10 h-10 text-[#6b7280] mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-heading text-xl font-bold">Not enough trades yet</div>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-md mx-auto">
            Once your EA closes a few trades (or you log manual notes with a P&amp;L), you&apos;ll see equity
            curves, win rates and edge breakdowns here.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
            <KPI label="Total P&L" value={money(summary.total_pnl)} tone={kpiTone.pnl} icon={TrendingUp} testId="kpi-total-pnl" />
            <KPI label="Trades" value={summary.total_trades} sub={`${summary.wins}W · ${summary.losses}L`} icon={Layers} testId="kpi-trades" />
            <KPI label="Win Rate" value={`${summary.win_rate_pct}%`} tone={summary.win_rate_pct >= 50 ? "buy" : "warn"} icon={Award} testId="kpi-winrate" />
            <KPI label="Profit Factor" value={summary.profit_factor === null ? "∞" : summary.profit_factor} tone={summary.profit_factor > 1 ? "buy" : "sell"} icon={Zap} testId="kpi-pf" />
            <KPI label="Expectancy" value={money(summary.expectancy_per_trade)} sub="per trade" tone={kpiTone.expect} icon={Flame} testId="kpi-expectancy" />
            <KPI label="Streaks" value={`${summary.max_consecutive_wins}W / ${summary.max_consecutive_losses}L`} sub="max" icon={CalendarDays} testId="kpi-streaks" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="vx-card p-4 xl:col-span-2" data-testid="equity-curve">
              <div className="flex items-center justify-between mb-3">
                <div className="vx-label">Equity Curve (cumulative P&L)</div>
                <div className="text-[11px] text-[#6b7280] font-mono">{equity.length} closed trades</div>
              </div>
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <AreaChart data={equity} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="t" stroke="#6b7280" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                    <YAxis stroke="#6b7280" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip content={<TooltipBox />} />
                    <ReferenceLine y={0} stroke="#374151" strokeDasharray="3 3" />
                    <Area type="monotone" dataKey="equity" stroke="#10b981" strokeWidth={2} fill="url(#eqGrad)" name="Equity" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="vx-card p-4 flex flex-col" data-testid="best-worst">
              <div className="vx-label mb-3">Extremes</div>
              <div className="grid grid-cols-1 gap-3">
                <div className="p-3 rounded border border-[#10b981]/40 bg-[#10b981]/10">
                  <div className="text-[10px] uppercase tracking-widest text-[#10b981] font-bold">Best trade</div>
                  <div className="font-mono text-xl font-bold mt-1 text-[#10b981]">{money(summary.best_trade_pnl)}</div>
                </div>
                <div className="p-3 rounded border border-[#ef4444]/40 bg-[#ef4444]/10">
                  <div className="text-[10px] uppercase tracking-widest text-[#ef4444] font-bold">Worst trade</div>
                  <div className="font-mono text-xl font-bold mt-1 text-[#ef4444]">{money(summary.worst_trade_pnl)}</div>
                </div>
                <div className="p-3 rounded border border-[#1f2937]">
                  <div className="vx-label mb-1">Averages</div>
                  <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                    <div>
                      <div className="text-[#6b7280]">Avg Win</div>
                      <div className="text-[#10b981] font-bold">{money(summary.avg_win)}</div>
                    </div>
                    <div>
                      <div className="text-[#6b7280]">Avg Loss</div>
                      <div className="text-[#ef4444] font-bold">{money(summary.avg_loss)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="vx-card p-4" data-testid="breakdown-panel">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="vx-label">Breakdown</div>
              <Tabs value={breakdownDim} onValueChange={setBreakdownDim} className="w-auto">
                <TabsList className="bg-[#0a0e1a] border border-[#1f2937]">
                  {DIMENSIONS.map((d) => (
                    <TabsTrigger
                      key={d.value}
                      value={d.value}
                      data-testid={`breakdown-tab-${d.value}`}
                      className="text-[11px] font-mono data-[state=active]:bg-[#3b82f6]/15 data-[state=active]:text-[#3b82f6]"
                    >
                      {d.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            {breakdown.length === 0 ? (
              <div className="py-12 text-center text-xs text-[#6b7280] font-mono">
                No data for this dimension in the current window.
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={breakdown} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="key" stroke="#6b7280" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                      <YAxis stroke="#6b7280" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip content={<TooltipBox />} />
                      <ReferenceLine y={0} stroke="#374151" />
                      <Bar dataKey="total_pnl" name="P&L">
                        {breakdown.map((r, i) => (
                          <Cell key={i} fill={r.total_pnl >= 0 ? "#10b981" : "#ef4444"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="breakdown-table">
                    <thead className="text-left">
                      <tr>
                        <th className="py-2 pr-2 vx-label">Key</th>
                        <th className="py-2 px-2 vx-label text-right">Trades</th>
                        <th className="py-2 px-2 vx-label text-right">Win %</th>
                        <th className="py-2 pl-2 vx-label text-right">P&L</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {breakdown.map((r) => (
                        <tr key={String(r.key)} className="border-t border-[#1f2937] hover:bg-[#1f2937]/50">
                          <td className="py-2 pr-2 font-bold">{String(r.key)}</td>
                          <td className="py-2 px-2 text-right">{r.trades_count} <span className="text-[#6b7280]">({r.wins}W/{r.losses}L)</span></td>
                          <td className="py-2 px-2 text-right">
                            <span style={{ color: r.win_rate >= 50 ? "#10b981" : "#f59e0b" }}>
                              {r.win_rate.toFixed(1)}%
                            </span>
                          </td>
                          <td className="py-2 pl-2 text-right font-bold" style={{ color: r.total_pnl >= 0 ? "#10b981" : "#ef4444" }}>
                            {money(r.total_pnl)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <BacktestWizard open={showBacktest} onClose={() => setShowBacktest(false)} />
      <WeeklyDigestModal open={showDigest} onClose={() => setShowDigest(false)} />
    </div>
  );
}
